import { Col, Row } from 'antd';
import config from 'config';
import { Routes } from 'config/routes';
import { format } from 'date-fns';
import { ProductOutput, WoocommerceProductCategoryOutput } from 'gql/types';
import { Link } from 'modules/common/components';
import { getCryoType } from 'modules/products/utils/getCryoType';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

import { CryoTag } from '../CryoTag';

type PropType = {
  product: ProductOutput;
  woocommerceProductCategory?: WoocommerceProductCategoryOutput;
};

const ProductDetails: FC<PropType> = ({
  product,
  woocommerceProductCategory,
}) => {
  const { t } = useTranslation('products.ProductDetails');

  const productBloodIdentifier = product.bloodIdentifiers?.[0];
  const cryoType = getCryoType(product.woocommerceProductId);

  const baseProductInfo: Record<string, ReactNode> = {
    [t('expirationDates')]: (
      <>
        {format(
          new Date(product.donation.donationDate),
          config.DATE_FORMATS.DATE
        )}
        <br />
        {format(new Date(product.expirationDate), config.DATE_FORMATS.DATE)}
      </>
    ),
    [t('productName')]: `${woocommerceProductCategory?.name}`,
  };

  const cryoInfo: Record<string, ReactNode> = cryoType
    ? { [t('composition')]: <CryoTag type={cryoType} /> }
    : {};

  const restProductInfo: Record<string, ReactNode> = {
    [t('bloodType')]: `${product.donation.donor.bloodType || '-'}`,
    [t('volume')]: `${product.value} ${t('ml')}`,
    [t('donorId')]: (
      <Link
        target="_blank"
        to={Routes.DonorsProfile.replace(
          ':id',
          String(product.donation.donor.shortId)
        )}
      >
        {product.donation.donor.shortId}
      </Link>
    ),
    [t('donationDate')]: format(
      new Date(product.donation.donationDate),
      config.DATE_FORMATS.SHORT_DATE
    ),
    [t('productId')]: `${product.shortId}`,
    [t('unitId')]: `${productBloodIdentifier?.unitId || '-'}`,
    [t('anticoagulant')]: product.anticoagulant,
    [t('status')]: product.status,
  };

  const productInfo: Record<string, ReactNode> = {
    ...baseProductInfo,
    ...cryoInfo,
    ...restProductInfo,
  };

  return (
    <Row gutter={[0, 15]}>
      {Object.keys(productInfo).map((key, i) => {
        return (
          <Col sm={12} span={24} key={`${key}_${i}`}>
            <Row justify="space-between" gutter={10}>
              <Col span={12} className={styles.label}>
                {key}:
              </Col>
              <Col span={12}>{productInfo[key]}</Col>
            </Row>
          </Col>
        );
      })}
    </Row>
  );
};

export default ProductDetails;
